import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Mail, Loader2, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { SocialSignIn } from "../components/SocialSignIn";

export const Login = () => {
    const { requestMagicLink, user } = useAuth();
    const [params] = useSearchParams();
    const nextPath = params.get("next") || "/profile";
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(null);  // { email, dev_link?, mocked }

    // Listen for "coming soon" clicks from disabled social buttons
    useEffect(() => {
        const handler = (e) => {
            const p = e?.detail?.provider || "This provider";
            toast(`${p} sign-in coming soon`, { description: "Magic-link or Google works right now." });
        };
        window.addEventListener("fitsme:social-soon", handler);
        return () => window.removeEventListener("fitsme:social-soon", handler);
    }, []);

    if (user) {
        return (
            <div className="max-w-md mx-auto py-20 px-6 text-center">
                <p className="font-mono text-xs uppercase tracking-widest mb-3">/ already signed in /</p>
                <p className="font-display text-3xl font-black mb-6">{user.email}</p>
                <Link to="/profile" data-testid="login-go-profile" className="inline-block border-4 border-black bg-[#CCFF00] px-6 py-3 font-mono text-xs uppercase tracking-widest shadow-brutal-sm">
                    Go to my profile →
                </Link>
            </div>
        );
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setSubmitting(true);
        try {
            const data = await requestMagicLink(email.trim(), nextPath);
            setSent(data);
            if (data.mocked) {
                toast("Dev mode: link returned in response", { description: "Add RESEND_API_KEY to send real emails." });
            } else {
                toast.success("Check your inbox", { description: `Sign-in link sent to ${data.email}` });
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send link");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-md mx-auto py-16 px-6">
            <div className="border-4 border-black bg-white shadow-brutal-lg">
                <div className="px-6 pt-6 pb-4 border-b-2 border-black bg-[#CCFF00]">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-black/70">/ Sign in · No password /</p>
                    <h1 className="font-display text-4xl font-black uppercase tracking-tight leading-none mt-1">
                        ONE TAP IN
                    </h1>
                </div>

                {sent ? (
                    <div className="p-6 space-y-4" data-testid="login-sent-state">
                        <div className="flex items-start gap-3">
                            <CheckCircle2 className="w-6 h-6 text-black mt-0.5" />
                            <div>
                                <p className="font-display text-lg font-black uppercase">Link sent</p>
                                <p className="font-mono text-xs mt-1">
                                    We sent a sign-in link to <span className="bg-[#FF00FF] text-white px-1 font-bold">{sent.email}</span>. It expires in {sent.expires_in_minutes || 15} minutes.
                                </p>
                            </div>
                        </div>
                        {sent.mocked && sent.dev_link && (
                            <div className="border-2 border-dashed border-black bg-[#FDFBF7] p-3">
                                <p className="font-mono text-[10px] uppercase tracking-widest text-black/60 mb-2">/ dev mode · no resend key /</p>
                                <a
                                    href={sent.dev_link}
                                    data-testid="login-dev-link"
                                    className="inline-flex items-center gap-2 break-all font-mono text-[11px] underline underline-offset-2 text-black"
                                >
                                    <ExternalLink className="w-3 h-3 shrink-0" />
                                    {sent.dev_link}
                                </a>
                            </div>
                        )}
                        <button
                            onClick={() => { setSent(null); setEmail(""); }}
                            data-testid="login-try-another"
                            className="font-mono text-xs uppercase tracking-widest underline underline-offset-4"
                        >
                            ← Try another email
                        </button>
                    </div>
                ) : (
                    <form onSubmit={onSubmit} className="p-6 space-y-4">
                        {/* Social sign-in (Google works; others marked SOON until creds arrive) */}
                        {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
                        <SocialSignIn nextPath={nextPath} />

                        {/* OR divider */}
                        <div className="flex items-center gap-3 py-1" data-testid="login-or-divider">
                            <span className="flex-1 border-t-2 border-black/30" />
                            <span className="font-mono text-[10px] uppercase tracking-widest text-black/60">OR</span>
                            <span className="flex-1 border-t-2 border-black/30" />
                        </div>

                        <p className="font-mono text-xs leading-relaxed">
                            Enter your email — we'll send a one-tap link. Your profile (measurements + selfie) will follow you across devices.
                        </p>
                        <div className="border-2 border-black flex items-center bg-white">
                            <span className="px-3 py-3 border-r-2 border-black bg-[#FDFBF7]">
                                <Mail className="w-4 h-4" />
                            </span>
                            <input
                                type="email"
                                required
                                autoFocus
                                inputMode="email"
                                autoComplete="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email-input"
                                className="flex-1 px-3 py-3 outline-none font-mono text-sm bg-transparent"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="login-submit-button"
                            className="w-full bg-black text-[#CCFF00] border-2 border-black px-4 py-3 font-display font-black uppercase tracking-widest shadow-brutal-sm disabled:opacity-60 flex items-center justify-center gap-2"
                        >
                            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Send me a link →"}
                        </button>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-black/50 text-center">
                            / We never store passwords /
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
};
